interface AnchorableItem {
	id: string;
	title: string;
}

export interface HashTarget {
	index: number;
	anchorId: string;
	/** False when the hash was written by hand and should be rewritten to `anchorId`. */
	isCanonical: boolean;
}

const slugify = (value: string): string =>
	(value ?? '')
		.toLowerCase()
		.normalize('NFC')
		// U+02BC is categorised as a letter, so apostrophes have to go before the sweep below
		// or they would survive it. Dropping them keeps a word whole: editor's -> editors.
		.replace(/['’‘ʼ´`]/g, '')
		// Everything that is not a letter or a number becomes a separator. That covers all the
		// punctuation, collapses runs, and catches every flavour of whitespace -- including the
		// non-breaking space a title pasted out of Word carries, which is invisible in the CMS.
		.replace(/[^\p{L}\p{N}]+/gu, '-')
		.replace(/^-+|-+$/g, '');

const decodeHash = (hash: string): string => {
	const value = hash.replace(/^#/, '');

	try {
		return decodeURIComponent(value).normalize('NFC');
	} catch {
		// A hand-written hash can hold a malformed escape sequence; take it verbatim.
		return value.normalize('NFC');
	}
};

/**
 * Builds the anchor id for an accordion item as `{slugified title}-{id}`, e.g.
 * `hvem-skal-jeg-kontakte-8141c5bb24e1`. The slug is cosmetic and changes whenever an
 * editor renames the item; the trailing id is what makes an existing link keep working.
 */
export const accordionItemAnchorId = (title: string, id: string): string => {
	const slug = slugify(title);

	return slug ? `${slug}-${id}` : id;
};

/** Only the trailing id segment is compared, so a renamed item keeps its old links alive. */
const hashMatchesId = (hash: string, id: string): boolean => {
	if (!id) return false;

	const anchorId = decodeHash(hash);

	return anchorId === id || anchorId.slice(anchorId.lastIndexOf('-') + 1) === id;
};

/** Compares slugs, so `#Hvem skal jeg kontakte?` and `#hvem-skal-jeg-kontakte` both hit. */
const hashMatchesTitle = (hash: string, title: string): boolean => {
	const slug = slugify(title);

	return !!slug && slugify(decodeHash(hash)) === slug;
};

/**
 * Points a url hash at one of the items. An id match wins, because it is the part that
 * survives a rename. Failing that the title is matched, which lets an editor link with
 * `/moduler/accordion#Hvem skal jeg kontakte?` — copied straight out of the CMS — instead
 * of having to dig the id out of the markup. Duplicate titles resolve to the first item.
 *
 * `isCanonical` tells the caller whether the hash already is the `{slug}-{id}` form, so a
 * title-only or renamed link can be swapped for the durable one in the address bar.
 */
export const resolveHashTarget = (items: AnchorableItem[], hash: string): HashTarget | null => {
	if (!hash) return null;

	const byId = items.findIndex((item) => hashMatchesId(hash, item.id));
	const index = byId !== -1 ? byId : items.findIndex((item) => hashMatchesTitle(hash, item.title));

	if (index === -1) return null;

	const anchorId = accordionItemAnchorId(items[index].title, items[index].id);

	return { index, anchorId, isCanonical: decodeHash(hash) === anchorId };
};
